import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, Typography, Skeleton } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DownloadIcon from "@mui/icons-material/Download";
import Header from "../../../components/navigation/Header";
import { downloadResultPdf, getMarksBreakdown } from "../../../utils/apiService";

const useStyles = makeStyles(() => ({
    header: {
        fontSize: "18px",
        fontWeight: 500,
    },
    container: {
        borderRadius: '8px',
        marginTop: '32px',
        minHeight: 'calc(100vh - 120px)',
        position: 'relative',
        background: '#ffffff',
        padding: '24px'
    },
    card: {
        border: "1px solid #E0E0E0",
        borderRadius: "12px",
        padding: "16px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "12px",
    },
    downloadBtn: {
        borderRadius: "12px",
        border: '0.5px solid #D9DBE1 !important',
        color: 'black !important',
        padding: "8px 16px",
        marginTop: '16px !important'
    },
    disabledBtn: {
        borderRadius: "8px",
        textTransform: "none",
        width: "100%",
        padding: "8px 16px",
        marginTop: '16px !important'
    },
}));

const GradeCard = () => {
    const classes = useStyles();
    const [attachments, setAttachments] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError("");
        getMarksBreakdown()
            .then((res) => {
                const response = Array.isArray(res?.data) ? res.data : [];
                const files = response.flatMap((sem: any, idx: number) => {
                    const semesterLabel = sem?.Semester ?? idx + 1;
                    return (sem?.attachments || []).map((att: any, fileIdx: number) => ({
                        id: `${semesterLabel}-${fileIdx}`,
                        semester: semesterLabel,
                        fileName: att?.fileName || "Grade Card",
                        fileExtension: att?.fileExtension || "",
                        fileSize: att?.fileSize,
                        downloadUrl: (att?.downloadUrl || att?.downloadURL || "").trim(),
                        attachmentId: att?.attachmentId || "",
                    }));
                });
                setAttachments(files);
            })
            .catch((err) => {
                console.error("Failed to load grade cards", err);
                setError("Unable to fetch grade cards right now.");
            })
            .finally(() => setLoading(false));
    }, []);

    const formatFileSize = useMemo(
        () => (size?: number) => {
            if (!size || Number.isNaN(size)) return "--";
            if (size < 1024) return `${size} B`;
            if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
            return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        },
        []
    );

    const handleDownload = async (file: any) => {
        if (!file?.attachmentId) {
            setError("No download reference found for this grade card.");
            return;
        }
        setError("");
        setDownloadingId(file.id);
        try {
            const blob = await downloadResultPdf(file.attachmentId);
            if (!blob) {
                throw new Error("No file returned");
            }
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const ext = file.fileExtension ? `.${file.fileExtension}` : "";
            link.download = `${file.fileName}${ext}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Grade card download failed", err);
            setError("Failed to download grade card. Please try again.");
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <Box >
            <Header title="Grade Cards" />
            <Box className={classes.container}>
                {loading ? (
                    <Grid container spacing={4}>
                        {Array.from({ length: 6 }).map((_, idx) => (
                            <Grid size={{ md: 3, sm: 4, xs: 6 }} key={idx}>
                                <Box className={classes.card}>
                                    <Skeleton variant="text" width="70%" height={24} />
                                    <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                ) : error ? (
                    <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
                ) : attachments.length > 0 ? (
                    <Grid container spacing={4}>
                        {attachments.map((file) => {
                            const isEnabled = Boolean(file.attachmentId);
                            return (
                                <Grid size={{ md: 3, sm: 4, xs: 12 }} key={file.id}>
                                    <Box className={classes.card}>
                                        <Box>
                                            <Typography className={classes.header}>
                                                Semester {file.semester}
                                            </Typography>
                                            <Typography sx={{ fontSize: 14, color: "#4B5563", mt: 0.5 }}>
                                                {file.fileName}.{file.fileExtension}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.5 }}>
                                                Size: {formatFileSize(file.fileSize)}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            sx={{ textTransform: "capitalize" }}
                                            fullWidth
                                            className={classes.downloadBtn}
                                            endIcon={<DownloadIcon />}
                                            onClick={() => handleDownload(file)}
                                            disabled={!isEnabled || downloadingId === file.id}
                                        >
                                            {downloadingId === file.id
                                                ? "Downloading..."
                                                : isEnabled
                                                    ? "Download grade card"
                                                    : "Not available"}
                                        </Button>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    <Box
                        sx={{
                            mt: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                        }}
                    >
                        <Typography sx={{ maxWidth: 420 }}>
                            Grade cards are not available right now.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default GradeCard;
